import React from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  Chip,
  Autocomplete,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import {
  CELL_CONDITION_LABELS,
  cellConditionOperators,
  cellConditionTypesForColumn,
} from '../../../utils/ingestionRuleValidation';

const CONDITION_TYPE_INPUTS = {
  always: { needsValue: false },
  is_empty: { needsValue: false },
  content_length: { needsOperator: true, needsValue: true, valueType: 'number' },
  content_type: { needsValue: true, valueType: 'select', options: ['date', 'number', 'text'] },
  contains: { needsValue: true, valueType: 'text' },
  is_contained_in: { needsValue: true, valueType: 'array' },
  numeric_comparison: { needsOperator: true, needsValue: true, valueType: 'number' },
};

// A choice the target column cannot run stays in the menu, labelled, rather than
// vanishing: the rule is already saved, Save Settings refuses it with the same
// reason, and a Select whose value is not among its options renders blank and
// leaves the user nothing to correct.
const withSupportLabel = (value, label, supported) => (
  supported ? label : `${label} — not valid for this column`
);

const CellConditionBuilder = ({ condition, onUpdateCondition, targetType = 'Text' }) => {
  // Only the conditions and operators this column's declared type supports. The
  // runtime raises on the rest (Content Length on a Number column, an ordering
  // comparison on a Text one), which fails the unit and every subsequent upload into
  // the table until someone finds the rule — so they are not offered here at all.
  const conditionType = condition.type || 'always';
  const supportedTypes = cellConditionTypesForColumn(targetType);
  const shownTypes = supportedTypes.includes(conditionType)
    ? supportedTypes
    : [...supportedTypes, conditionType];
  const conditionTypes = shownTypes.map(value => ({
    value,
    label: withSupportLabel(
      value, CELL_CONDITION_LABELS[value] || value, supportedTypes.includes(value)),
    ...CONDITION_TYPE_INPUTS[value],
  }));
  const selectedType = conditionTypes.find(type => type.value === conditionType);

  const selectedOperator = condition.operator || '==';
  const supportedOperators = cellConditionOperators(conditionType, targetType);
  const shownOperators = supportedOperators.includes(selectedOperator)
    ? supportedOperators
    : [...supportedOperators, selectedOperator];
  const operatorOptions = shownOperators.map(value => ({
    value,
    label: withSupportLabel(value, value, supportedOperators.includes(value)),
  }));

  return (
    <Box>
      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
        <InputLabel>Condition Type</InputLabel>
        <Select
          value={conditionType}
          onChange={(e) => onUpdateCondition({
            ...condition, type: e.target.value, operator: '==', value: null, values: [],
          })}
          label="Condition Type"
        >
          {conditionTypes.map(type => (
            <MenuItem key={type.value} value={type.value}>
              {type.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {selectedType?.needsOperator && (
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel>Operator</InputLabel>
          <Select
            value={selectedOperator}
            onChange={(e) => onUpdateCondition({
              ...condition, operator: e.target.value, value: null, values: [],
            })}
            label="Operator"
          >
            {operatorOptions.map(option => (
              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {selectedType?.needsValue && (
        <>
          {selectedType.valueType === 'select' ? (
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Value</InputLabel>
              <Select
                value={condition.value || ''}
                onChange={(e) => onUpdateCondition({ ...condition, value: e.target.value })}
                label="Value"
              >
                {selectedType.options.map(opt => (
                  <MenuItem key={opt} value={opt}>
                    {opt.charAt(0).toUpperCase() + opt.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : selectedType.valueType === 'array' ? (
            <Autocomplete
              multiple
              freeSolo
              options={[]}
              value={condition.values || []}
              onChange={(_, newValue) => onUpdateCondition({ ...condition, values: newValue })}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Allowed Values"
                  placeholder="Type and press Enter"
                  size="small"
                  helperText="Enter allowed values one by one"
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip size="small" label={option} {...getTagProps({ index })} />
                ))
              }
              sx={{ mb: 2 }}
            />
          ) : (
            <TextField
              fullWidth
              size="small"
              label={conditionType === 'content_length' ? 'Character count' : 'Value'}
              type={selectedType.valueType === 'number' ? 'number' : 'text'}
              inputProps={conditionType === 'content_length' ? { min: 0, step: 1 } : undefined}
              value={condition.value ?? ''}
              onChange={(e) => onUpdateCondition({ ...condition, value: e.target.value })}
              sx={{ mb: 2 }}
            />
          )}
        </>
      )}

      <FormControlLabel
        control={
          <Checkbox
            checked={condition.negate || false}
            onChange={(e) => onUpdateCondition({ ...condition, negate: e.target.checked })}
            size="small"
          />
        }
        label={
          <Typography variant="body2" color={condition.negate ? 'error.main' : 'text.secondary'}>
            Negate condition (NOT)
          </Typography>
        }
        sx={{ mb: 2 }}
      />
    </Box>
  );
};

export default CellConditionBuilder;
